/**
 * AddCustomerModal (NEWCUST-UI, Seth 2026-08-20: "under customers —
 * shouldn't there be an add? … Whatever has been entered would be
 * autopopulated and checked against google maps to properly enter address.")
 *
 * Creates a PEGASUS-NATIVE customer (pegasus_customer, custno "N000123").
 * ESC stays untouched (read-only, DEC-22) — native customers live beside the
 * mirror and show up in search + the 360 through the existing NEWCUST paths.
 *
 * Address check: uses the SAME Google key the 360's property photos use
 * (VITE_GOOGLE_MAPS_KEY) via the Geocoding API — you get Google's formatted
 * address as a SUGGESTION to accept or ignore, and every field (city
 * included) stays hand-editable after. Propose, never force.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { UserPlus, MapPin, Check, X, Loader2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/cn";
import { AddressAutocomplete } from "./AddressAutocomplete";
import { AlertTriangle, UserRound } from "lucide-react";
import { useActiveTechs } from "@/features/board/api";

interface GeoSuggestion {
  formatted: string;
  add1: string;
  city: string;
  state: string;
  zip: string;
}

async function geocode(address: string): Promise<GeoSuggestion | null> {
  const key = import.meta.env.VITE_GOOGLE_MAPS_KEY;
  if (!key) return null;
  const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&components=administrative_area:MN|country:US&key=${key}`;
  const res = await fetch(url);
  if (!res.ok) return null;
  const data = await res.json();
  const hit = data?.results?.[0];
  if (!hit) return null;
  const comp = (type: string, short = false): string => {
    const c = (hit.address_components as { types: string[]; long_name: string; short_name: string }[] | undefined)?.find((x) => x.types.includes(type));
    return c ? (short ? c.short_name : c.long_name) : "";
  };
  const streetNo = comp("street_number");
  const route = comp("route");
  return {
    formatted: String(hit.formatted_address ?? ""),
    add1: [streetNo, route].filter(Boolean).join(" "),
    city: comp("locality") || comp("sublocality") || "",
    state: comp("administrative_area_level_1", true) || "MN",
    zip: comp("postal_code"),
  };
}

function useAddCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      firstname: string; lastname: string; phone: string; email: string;
      add1: string; city: string; state: string; zip: string;
      billName?: string; billAdd1?: string; billCity?: string; billState?: string; billZip?: string;
      /** TECH-REQUEST-AT-ADD (Seth 2026-10-22: "a first-time caller requesting a tech
       *  for their future appointments — where is this set?"): the requested tech
       *  goes on the record as the same confirmed `tech_pref` watch-out the Watch
       *  For card and Book a Call write, so the header badge, the grid and the
       *  tech app read it from day one. */
      requestTech?: { empno: string; first: string } | null;
    }): Promise<string> => {
      const who = (await supabase.auth.getUser()).data.user?.email ?? "office";
      // Next native number: N + 6 digits. Small shop, retry once on a race.
      const nextNo = async (): Promise<string> => {
        const { data } = await supabase.from("pegasus_customer").select("custno").like("custno", "N%").order("custno", { ascending: false }).limit(1);
        const maxN = data?.[0]?.custno ? parseInt(String(data[0].custno).slice(1), 10) || 0 : 0;
        return "N" + String(maxN + 1).padStart(6, "0");
      };
      const row = (custno: string) => ({
        custno,
        firstname: v.firstname.trim() || null,
        lastname: v.lastname.trim() || null,
        fullname: [v.firstname.trim(), v.lastname.trim()].filter(Boolean).join(" ") || v.phone || "New customer",
        phone: v.phone.trim() || null,
        email: v.email.trim() || null,
        add1: v.add1.trim() || null,
        city: v.city.trim() || null,
        state: v.state.trim() || "MN",
        zip: v.zip.trim() || null,
        // CUST-BILL-01: separate billing address (property manager / landlord).
        bill_name: v.billName?.trim() || null,
        bill_add1: v.billAdd1?.trim() || null,
        bill_city: v.billCity?.trim() || null,
        bill_state: v.billState?.trim() || null,
        bill_zip: v.billZip?.trim() || null,
        source: "office",
        active: true,
        created_by: who,
      });
      let custno = await nextNo();
      let { error } = await supabase.from("pegasus_customer").insert(row(custno));
      if (error && error.code === "23505") {
        custno = await nextNo();
        ({ error } = await supabase.from("pegasus_customer").insert(row(custno)));
      }
      if (error) throw error;
      if (v.requestTech) {
        const now = new Date().toISOString();
        // Best-effort: the customer exists either way; a failure here is a missing watch-out, not a lost customer.
        await supabase.from("extracted_signal").insert({
          custno, locno: null, signal_type: "tech_pref", value: v.requestTech.first,
          detail: { source: "new_customer", empnos: [v.requestTech.empno] },
          source: "manual", status: "confirmed", confidence: 1,
          confirmed_by: who, confirmed_at: now, created_at: now, updated_at: now,
        });
      }
      return custno;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["customer_search"] }),
  });
}

/** CALLS-AI-01: a full prefill (from the call-intel chips) — every field the
 *  caller stated, seeded into the form. Still just a draft: nothing saves
 *  until the person hits Create. */
export interface CustomerSeed {
  firstname?: string;
  lastname?: string;
  phone?: string;
  email?: string;
  add1?: string;
  city?: string;
  state?: string;
  zip?: string;
}

export function AddCustomerModal({ initialQuery, seed, onClose, onCreated }: {
  initialQuery?: string;
  seed?: CustomerSeed;
  onClose: () => void;
  /** NEW-CALLER-01 (Seth 2026-09-30: "no match for the address… no way to
   *  create a customer from there"): when set, creating hands the new
   *  customer BACK to the caller (the dispatch flow continues with them
   *  picked) instead of navigating to the 360. */
  onCreated?: (c: { custno: string; name: string; address: string | null; city: string | null; phone: string | null }) => void;
}) {
  const add = useAddCustomer();
  const navigate = useNavigate();

  // A search that found nothing is usually a name, an address, OR a phone
  // number — prefill the RIGHT field so nothing typed is wasted. Phone check
  // comes first: a phone starts with a digit too, and before CALLS-CREATE-01
  // (Seth 2026-08-28) a caller's number from the live call dock was landing
  // in the street-address field.
  const q = (initialQuery ?? "").trim();
  const qDigits = q.replace(/\D/g, "");
  const qP10 = qDigits.length === 11 && qDigits.startsWith("1") ? qDigits.slice(1) : qDigits;
  const looksLikePhone = qP10.length >= 7 && !/[a-z]/i.test(q);
  const looksLikeAddress = !looksLikePhone && /^\d/.test(q);
  const nameParts = !looksLikePhone && !looksLikeAddress && q ? q.split(/\s+/) : [];

  const [firstname, setFirstname] = useState(seed?.firstname ?? nameParts[0] ?? "");
  const [lastname, setLastname] = useState(seed?.lastname ?? nameParts.slice(1).join(" "));
  const [phone, setPhone] = useState(
    seed?.phone ?? (looksLikePhone ? (qP10.length === 10 ? `${qP10.slice(0, 3)}-${qP10.slice(3, 6)}-${qP10.slice(6)}` : q) : ""),
  );
  const [email, setEmail] = useState(seed?.email ?? "");
  const [add1, setAdd1] = useState(seed?.add1 ?? (looksLikeAddress ? q : ""));
  const [city, setCity] = useState(seed?.city ?? "");
  const [state, setState] = useState(seed?.state ?? "MN");
  const setStatevSafe = setState;
  const [zip, setZip] = useState(seed?.zip ?? "");
  const [sugg, setSugg] = useState<GeoSuggestion | null>(null);
  const [checking, setChecking] = useState(false);
  const [checkedNone, setCheckedNone] = useState(false);
  // CUST-BILL-01: billing address when it differs from the service address.
  const [billOpen, setBillOpen] = useState(false);
  const [billName, setBillName] = useState("");
  const [billAdd1, setBillAdd1] = useState("");
  const [billCity, setBillCity] = useState("");
  const [billState, setBillState] = useState("MN");
  const [billZip, setBillZip] = useState("");
  const { data: techs } = useActiveTechs("03");
  const [requestTech, setRequestTech] = useState<string>("");

  // DEDUP-AT-ENTRY (Seth 2026-08-28): same matcher the inbox uses — phone +
  // address against ESC AND Pegasus. Warn + one-click jump, never block
  // (Salesforce-style alert rules; an override is sometimes legitimate).
  const { data: dupes } = useQuery({
    queryKey: ["cust_dupes_add", phone, add1, city],
    enabled: !!(phone.trim().length >= 7 || add1.trim().length >= 5),
    staleTime: 15 * 1000,
    queryFn: async () => {
      const { data } = await supabase.rpc("find_customer_matches", {
        p_phone: phone, p_add1: add1, p_city: city,
      });
      return (data ?? []) as { custno: string; name: string; address: string; src: string; match_on: string }[];
    },
  });

  const hasMapsKey = Boolean(import.meta.env.VITE_GOOGLE_MAPS_KEY);
  const canCheck = add1.trim().length >= 4;
  const canSave = (firstname.trim() || lastname.trim() || phone.trim()) !== "";

  const runCheck = async () => {
    setChecking(true); setCheckedNone(false); setSugg(null);
    try {
      const s = await geocode([add1, city, state, zip].filter(Boolean).join(", "));
      if (s && s.add1) setSugg(s);
      else setCheckedNone(true);
    } finally { setChecking(false); }
  };
  const applySugg = () => {
    if (!sugg) return;
    if (sugg.add1) setAdd1(sugg.add1);
    if (sugg.city) setCity(sugg.city);
    if (sugg.state) setState(sugg.state);
    if (sugg.zip) setZip(sugg.zip);
    setSugg(null);
  };

  const submit = () =>
    add.mutate(
      {
        firstname, lastname, phone, email, add1, city, state, zip,
        ...(billOpen ? { billName, billAdd1, billCity, billState, billZip } : {}),
        requestTech: requestTech
          ? (() => { const t = (techs ?? []).find((x) => x.empno === requestTech); return t ? { empno: t.empno, first: ((t.firstname ?? t.empname ?? "").trim().split(" ")[0] || t.empname || t.empno) as string } : null; })()
          : null,
      },
      {
        onSuccess: (custno) => {
          onClose();
          if (onCreated) {
            // NEW-CALLER-01: back to whatever flow needed this customer.
            onCreated({
              custno,
              name: [firstname, lastname].filter((s) => s.trim()).join(" ").trim() || phone.trim() || "New Customer",
              address: add1.trim() || null,
              city: city.trim() || null,
              phone: phone.trim() || null,
            });
          } else {
            navigate(`/customers/${custno}`);
          }
        },
      },
    );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="flex items-center gap-2 text-base font-bold text-slate-900">
          <UserPlus className="h-4 w-4 text-brand-600" /> Add a Customer
        </h3>
        <p className="mt-1 text-sm text-slate-500">
          Creates a Pegasus customer — ESC isn&apos;t touched. They&apos;re searchable and get a full 360 immediately.
        </p>

        <div className="mt-4 grid grid-cols-2 gap-2.5">
          <Fld label="First name"><input value={firstname} onChange={(e) => setFirstname(e.target.value)} autoFocus className="ainp" /></Fld>
          <Fld label="Last name"><input value={lastname} onChange={(e) => setLastname(e.target.value)} className="ainp" /></Fld>
          <Fld label="Phone"><input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="612-555-0100" className="ainp" /></Fld>
          <Fld label="Email"><input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@email.com" className="ainp" /></Fld>
          <div className="col-span-2"><Fld label="Street address">
            <AddressAutocomplete
              value={add1}
              onChange={(v) => { setAdd1(v); setSugg(null); setCheckedNone(false); }}
              onResolved={(a) => { setAdd1(a.add1); if (a.city) setCity(a.city); if (a.state) setStatevSafe(a.state); if (a.zip) setZip(a.zip); }}
              placeholder="14837 Crestview Avenue W — Google suggests as you type"
              className="ainp"
            />
          </Fld></div>
          <Fld label="City"><input value={city} onChange={(e) => setCity(e.target.value)} className="ainp" /></Fld>
          <div className="grid grid-cols-2 gap-2.5">
            <Fld label="State"><input value={state} onChange={(e) => setState(e.target.value)} className="ainp" /></Fld>
            <Fld label="Zip"><input value={zip} onChange={(e) => setZip(e.target.value)} className="ainp" /></Fld>
          </div>
        </div>

        {/* TECH-REQUEST-AT-ADD — "Jeremy only," said on the first call. */}
        <div className="mt-3 flex flex-wrap items-center gap-2.5 rounded-lg border border-hairline bg-slate-50/60 px-3 py-2">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600"><UserRound className="h-3.5 w-3.5 text-amber-600" /> Requests a Tech</span>
          <select value={requestTech} onChange={(e) => setRequestTech(e.target.value)} className="ainp !w-auto !py-1 text-sm">
            <option value="">No preference</option>
            {(techs ?? []).slice().sort((a, b) => (a.firstname ?? "").localeCompare(b.firstname ?? "")).map((t) => (
              <option key={t.empno} value={t.empno}>{(t.firstname ?? t.empname ?? "").trim().split(" ")[0] || t.empname}{t.lastname ? ` ${t.lastname}` : ""}</option>
            ))}
          </select>
          <span className="text-[11px] text-slate-400">Goes on the record as a watch-out — the badge, Book a Call and the tech app read it.</span>
        </div>

        {/* CUST-BILL-01 — bills go somewhere else (PM / landlord / builder). */}
        {!billOpen ? (
          <button type="button" onClick={() => setBillOpen(true)}
            className="mt-3 block text-left text-xs font-semibold text-brand-600 underline-offset-2 hover:underline">
            + Billing is different from the service address (property manager, landlord, builder)
          </button>
        ) : (
          <div className="mt-3 space-y-2.5 rounded-lg border border-hairline bg-slate-50/60 p-3">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Billing address</span>
              <button type="button" onClick={() => setBillOpen(false)} className="text-[11px] text-slate-400 hover:text-slate-600">same as service — remove</button>
            </div>
            <Fld label="Bill to (name / company)"><input value={billName} onChange={(e) => setBillName(e.target.value)} placeholder="e.g. Oakwood Property Management" className="ainp" /></Fld>
            <Fld label="Street address"><input value={billAdd1} onChange={(e) => setBillAdd1(e.target.value)} className="ainp" /></Fld>
            <div className="grid grid-cols-3 gap-2.5">
              <Fld label="City"><input value={billCity} onChange={(e) => setBillCity(e.target.value)} className="ainp" /></Fld>
              <Fld label="State"><input value={billState} onChange={(e) => setBillState(e.target.value)} className="ainp" /></Fld>
              <Fld label="Zip"><input value={billZip} onChange={(e) => setBillZip(e.target.value)} className="ainp" /></Fld>
            </div>
          </div>
        )}

        {/* Google address check — propose, never force. City stays editable. */}
        {hasMapsKey && (
          <div className="mt-3">
            <button type="button" disabled={!canCheck || checking} onClick={runCheck}
              className="inline-flex items-center gap-1.5 rounded-md bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-200 disabled:opacity-40">
              {checking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MapPin className="h-3.5 w-3.5" />} Check Address With Google
            </button>
            {sugg && (
              <div className="mt-2 rounded-lg border border-sky-200 bg-sky-50/60 p-3">
                <p className="text-sm text-slate-700"><span className="font-semibold">Google suggests:</span> {sugg.formatted}</p>
                <div className="mt-2 flex items-center gap-2">
                  <button type="button" onClick={applySugg}
                    className="inline-flex items-center gap-1 rounded-md bg-brand-700 px-2.5 py-1 text-xs font-semibold text-white hover:bg-brand-800">
                    <Check className="h-3.5 w-3.5" /> Use This
                  </button>
                  <button type="button" onClick={() => setSugg(null)}
                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-slate-500 hover:bg-white">
                    <X className="h-3.5 w-3.5" /> Keep What I Typed
                  </button>
                  <span className="text-[11px] text-slate-400">You can still edit any field (city included) after.</span>
                </div>
              </div>
            )}
            {checkedNone && <p className="mt-1.5 text-xs text-amber-700">Google couldn&apos;t match that address — double-check the street, or save it as typed.</p>}
          </div>
        )}

        {dupes && dupes.length > 0 && (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <div className="flex items-center gap-1.5 font-semibold">
              <AlertTriangle className="h-3.5 w-3.5" /> Might already be in the system — open instead of creating a duplicate:
            </div>
            <ul className="mt-1 space-y-0.5">
              {dupes.slice(0, 4).map((dd) => (
                <li key={dd.custno}>
                  <button type="button" onClick={() => { onClose(); navigate(`/customers/${dd.custno}`); }}
                    className="text-left underline-offset-2 hover:underline">
                    {dd.name} · {dd.address || dd.custno} <span className="text-amber-700">({dd.src}, matched on {dd.match_on})</span>
                  </button>
                </li>
              ))}
            </ul>
            <p className="mt-1 text-[11px] text-amber-700">Same house, new owner? Open the record and use Transfer Ownership — the house keeps its history.</p>
          </div>
        )}
        {add.isError && <p className="mt-2 text-xs font-medium text-rose-600">Couldn&apos;t create the customer — try again.</p>}

        <div className="mt-4 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-md px-3 py-1.5 text-sm text-slate-500 hover:bg-slate-100">Cancel</button>
          <button type="button" disabled={!canSave || add.isPending} onClick={submit}
            className={cn("inline-flex items-center gap-1.5 rounded-md bg-brand-700 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand-800 disabled:opacity-50")}>
            {add.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Create Customer
          </button>
        </div>
        <style>{`.ainp{width:100%;border:1px solid var(--hairline,#e2e8f0);border-radius:0.375rem;padding:0.4rem 0.6rem;font-size:0.875rem}.ainp:focus{outline:none;border-color:#3b82f6;box-shadow:0 0 0 2px rgba(59,130,246,.25)}`}</style>
      </div>
    </div>
  );
}

function Fld({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block text-[11px] font-medium text-slate-500">{label}</span>
      {children}
    </label>
  );
}
